import { GIFEncoder, applyPalette, quantize } from "gifenc";

/**
 * Konkord: turning a stretch of video into an animated GIF that fits the
 * upload limit (avatar 4 MB, banner 6 MB, emoji 500 KB...). Frames are taken
 * once at full size and frame rate; when the GIF comes out too big, it is
 * encoded again with fewer frames, fewer colours and then a smaller size.
 */

/**
 * Frames taken from the video, RGBA, all the same size
 */
export interface Frames {
  width: number;
  height: number;
  fps: number;
  data: Uint8ClampedArray[];
}

/**
 * One way of encoding: frame rate, colours and size (1 = full)
 */
export interface Plan {
  fps: number;
  colors: number;
  scale: number;
}

/** Konkord: frame rate the frames are taken at, the best plan uses it all */
export const BASE_FPS = 20;

/**
 * From best to smallest. Frame rate goes first (barely noticed down to
 * 12), then colours, and size last (the most visible).
 */
export const PLANS: Plan[] = [
  { fps: 20, colors: 256, scale: 1 },
  { fps: 15, colors: 256, scale: 1 },
  { fps: 12, colors: 256, scale: 1 },
  { fps: 12, colors: 128, scale: 1 },
  { fps: 10, colors: 128, scale: 1 },
  { fps: 10, colors: 64, scale: 1 },
  { fps: 10, colors: 64, scale: 0.85 },
  { fps: 10, colors: 64, scale: 0.7 },
  { fps: 8, colors: 64, scale: 0.6 },
  { fps: 8, colors: 32, scale: 0.5 },
  { fps: 6, colors: 32, scale: 0.4 },
];

/**
 * Which of the frames (taken at `from` fps) to keep for `to` fps
 */
export function pickFrames(count: number, from: number, to: number) {
  if (to >= from) return Array.from({ length: count }, (_, i) => i);
  const picked: number[] = [];
  const step = from / to;
  for (let at = 0; Math.round(at) < count; at += step) {
    picked.push(Math.round(at));
  }
  return picked;
}

/**
 * Encode frames as a looping GIF with one palette for all of them (no
 * colour flicker between frames, and smaller than a palette per frame)
 */
export function encodeGif(
  frames: Uint8ClampedArray[],
  width: number,
  height: number,
  fps: number,
  colors: number,
) {
  // the palette comes from a sample of the frames, enough to see every scene
  const sampleEvery = Math.max(1, Math.floor(frames.length / 8));
  const sample = frames.filter((_, i) => i % sampleEvery === 0);
  const pixels = new Uint8ClampedArray(sample.length * width * height * 4);
  sample.forEach((frame, i) => pixels.set(frame, i * width * height * 4));
  const palette = quantize(pixels, colors);

  const gif = GIFEncoder();
  const delay = 1000 / fps;
  frames.forEach((frame, i) => {
    gif.writeFrame(applyPalette(frame, palette), width, height, {
      palette: i === 0 ? palette : undefined,
      delay,
      repeat: 0,
    });
  });
  gif.finish();
  return gif.bytes();
}

/**
 * Try each plan from the best until the GIF fits. `resize` scales a frame
 * (done with a canvas in the browser). Gives up with the smallest one.
 */
export async function fitGif(
  frames: Frames,
  maxBytes: number,
  resize: (
    frame: Uint8ClampedArray,
    width: number,
    height: number,
    toWidth: number,
    toHeight: number,
  ) => Uint8ClampedArray | Promise<Uint8ClampedArray>,
  onTry?: (plan: Plan, index: number) => void,
) {
  let last: { bytes: Uint8Array; plan: Plan; width: number; height: number };
  for (const [index, plan] of PLANS.entries()) {
    onTry?.(plan, index);
    const width = Math.max(16, Math.round(frames.width * plan.scale));
    const height = Math.max(16, Math.round(frames.height * plan.scale));
    const picked = pickFrames(frames.data.length, frames.fps, plan.fps).map(
      (i) => frames.data[i],
    );
    const sized =
      plan.scale === 1
        ? picked
        : await Promise.all(
            picked.map((frame) =>
              resize(frame, frames.width, frames.height, width, height),
            ),
          );
    const bytes = encodeGif(
      sized,
      width,
      height,
      Math.min(plan.fps, frames.fps),
      plan.colors,
    );
    last = { bytes, plan, width, height };
    // a little margin: the server counts the whole upload
    if (bytes.length <= maxBytes * 0.97) return { ...last, fits: true };
  }
  return { ...last!, fits: false };
}

/**
 * Output size for a crop ratio: square ones are avatars, icons and emojis
 * (emojis, with their small limit, get 128 px), wide ones are banners
 */
export function outputSize(ratio: number, maxBytes: number | undefined) {
  if (ratio === 1) {
    const side = maxBytes !== undefined && maxBytes <= 1_000_000 ? 128 : 256;
    return { width: side, height: side };
  }
  const width = 480;
  return { width, height: Math.round(width / ratio) };
}

/** Konkord: longest stretch of video that can be turned into a GIF */
export function maxSeconds(maxBytes: number | undefined) {
  return maxBytes !== undefined && maxBytes <= 1_000_000 ? 4 : 6;
}

/**
 * Konkord: nearest palette colour, cached by 15-bit colour (photos repeat
 * colours a lot, so most lookups are one read)
 */
function nearestFinder(palette: number[][]) {
  const cache = new Int16Array(32768).fill(-1);
  return (r: number, g: number, b: number) => {
    const key = ((r >> 3) << 10) | ((g >> 3) << 5) | (b >> 3);
    const cached = cache[key];
    if (cached >= 0) return cached;
    let best = 0;
    let bestDistance = Infinity;
    for (let i = 0; i < palette.length; i++) {
      const dr = r - palette[i][0];
      const dg = g - palette[i][1];
      const db = b - palette[i][2];
      const distance = dr * dr + dg * dg + db * db;
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    }
    cache[key] = best;
    return best;
  };
}

/**
 * Floyd–Steinberg dithering: spreads each pixel's colour error to its
 * neighbours, so a photo in 256 colours keeps smooth skin and skies
 * instead of bands
 */
export function ditherToPalette(
  rgba: Uint8ClampedArray,
  palette: number[][],
  width: number,
  height: number,
) {
  const nearest = nearestFinder(palette);
  const work = new Float32Array(width * height * 3);
  for (let i = 0, j = 0; i < rgba.length; i += 4, j += 3) {
    work[j] = rgba[i];
    work[j + 1] = rgba[i + 1];
    work[j + 2] = rgba[i + 2];
  }
  const index = new Uint8Array(width * height);
  const spread = (x: number, y: number, e: number[], share: number) => {
    if (x < 0 || x >= width || y >= height) return;
    const j = (y * width + x) * 3;
    work[j] += e[0] * share;
    work[j + 1] += e[1] * share;
    work[j + 2] += e[2] * share;
  };
  for (let y = 0; y < height; y++)
    for (let x = 0; x < width; x++) {
      const p = y * width + x;
      const j = p * 3;
      const r = Math.min(255, Math.max(0, Math.round(work[j])));
      const g = Math.min(255, Math.max(0, Math.round(work[j + 1])));
      const b = Math.min(255, Math.max(0, Math.round(work[j + 2])));
      const chosen = nearest(r, g, b);
      index[p] = chosen;
      const error = [
        r - palette[chosen][0],
        g - palette[chosen][1],
        b - palette[chosen][2],
      ];
      spread(x + 1, y, error, 7 / 16);
      spread(x - 1, y + 1, error, 3 / 16);
      spread(x, y + 1, error, 5 / 16);
      spread(x + 1, y + 1, error, 1 / 16);
    }
  return index;
}

/**
 * Konkord: a GIF for a still picture with small moving effects. The first
 * frame is the whole picture with its own dithered palette; the others only
 * carry the pixels that changed (the rest transparent, kept from before),
 * each with a palette of its own. The photo keeps far better colours than
 * with one palette shared by every frame, and the file stays small.
 */
export function encodeOverlayGif(
  frames: Uint8ClampedArray[],
  width: number,
  height: number,
  fps: number,
) {
  const gif = GIFEncoder();
  const delay = 1000 / fps;
  const first = quantize(frames[0], 256);
  gif.writeFrame(
    ditherToPalette(frames[0], first, width, height),
    width,
    height,
    {
      palette: first,
      delay,
      repeat: 0,
    },
  );

  for (let f = 1; f < frames.length; f++) {
    const frame = frames[f];
    const previous = frames[f - 1];
    const changed: number[] = [];
    for (let i = 0; i < frame.length; i += 4) {
      if (
        frame[i] !== previous[i] ||
        frame[i + 1] !== previous[i + 1] ||
        frame[i + 2] !== previous[i + 2]
      )
        changed.push(i);
    }
    const index = new Uint8Array(width * height);
    // index 0 is transparent: "keep what is there"
    let palette: number[][] = [[0, 0, 0]];
    if (changed.length) {
      const pixels = new Uint8ClampedArray(changed.length * 4);
      changed.forEach((at, n) => pixels.set(frame.subarray(at, at + 4), n * 4));
      const colours = quantize(pixels, 255);
      const nearest = nearestFinder(colours);
      for (const at of changed)
        index[at / 4] = 1 + nearest(frame[at], frame[at + 1], frame[at + 2]);
      palette = [[0, 0, 0], ...colours];
    }
    gif.writeFrame(index, width, height, {
      palette,
      delay,
      transparent: true,
      transparentIndex: 0,
      dispose: 1,
    });
  }
  gif.finish();
  return gif.bytes();
}
